import { Injectable, inject, signal, computed, DestroyRef } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { firstValueFrom, filter, map } from 'rxjs';
import { VoiceConfig, DEFAULT_VOICE_CONFIG } from '../models';
import { ProjectService } from '../../../modules/project-management/services/project.service';

export interface ProjectMemberInfo {
  profileId: string;
  firstName: string;
  lastName: string;
  email: string;
}

export type VoiceModule = 'calendar' | 'project' | 'rooms' | 'inventory' | null;

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-/i;

/**
 * Segments statiques de /project-management/* qui ne sont PAS un projet (cf. les routes
 * declarees avant `:id` dans project-management.route.ts). Sans cette liste, une navigation
 * vers l'une de ces pages declenche un getProjectByIdentifier() sur le nom de la route, qui
 * echoue en 406 (PGRST116 : `.single()` sur 0 ligne) — erreur silencieuse cote code (catch),
 * mais visible dans la console reseau. A completer si une nouvelle route statique est ajoutee
 * a ce niveau.
 */
const NON_PROJECT_SEGMENTS = new Set(['access-denied', 'claude-connector']);

/**
 * Service de contexte vocal
 * Detecte le module actif et expose le contexte courant (projet, membres)
 */
@Injectable({ providedIn: 'root' })
export class VoiceContextService {
  private router = inject(Router);
  private projectService = inject(ProjectService);
  private destroyRef = inject(DestroyRef);

  private _projectMembers = signal<ProjectMemberInfo[]>([]);
  private _currentProjectId = signal<string | null>(null);
  private _currentProjectName = signal<string | null>(null);

  private currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );

  activeModule = computed<VoiceModule>(() => {
    const url = this.currentUrl();
    if (url.startsWith('/calendar')) return 'calendar';
    if (url.startsWith('/project-management')) return 'project';
    // MOKA-208 P4 : salles et inventaire n'étaient pas reconnus — seuls les
    // prompts calendrier/projet en dépendaient jusqu'ici. Ils servent désormais
    // à choisir les actions proposées à l'ouverture de l'assistant.
    if (url.startsWith('/room-reservations')) return 'rooms';
    if (url.startsWith('/materials')) return 'inventory';
    return null;
  });

  currentProjectId = computed(() => this._currentProjectId());
  currentProjectName = computed(() => this._currentProjectName());
  projectMembers = computed(() => this._projectMembers());

  /** Config audio differenciee par module */
  moduleVoiceConfig = computed<Partial<VoiceConfig>>(() => {
    const mod = this.activeModule();
    if (mod === 'project') {
      return { maxRecordingDuration: 90, silenceDuration: 10000 };
    }
    return {};
  });

  /** Indique si le bouton vocal doit etre visible */
  /**
   * L'assistant conversationnel est accessible sur toutes les pages (MOKA-204) :
   * l'edge function embarque les prompts de tous les modules, `activeModule` ne
   * sert plus que d'indice de contexte. Le gating d'abonnement reste porté par
   * subFeatures.voiceEnabled côté header.
   */
  isVoiceEnabled = computed(() => true);

  constructor() {
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => this.extractProjectParam(event.urlAfterRedirects)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(param => {
      if (param) {
        this.resolveAndLoadProject(param);
      } else {
        this._currentProjectId.set(null);
        this._currentProjectName.set(null);
        this._projectMembers.set([]);
      }
    });

    // Handle initial URL
    const initialParam = this.extractProjectParam(this.router.url);
    if (initialParam) {
      this.resolveAndLoadProject(initialParam);
    }
  }

  /**
   * Extrait le paramètre projet de l'URL (UUID ou identifiant court)
   * Exclut les routes statiques qui ne sont pas des projets (cf. NON_PROJECT_SEGMENTS)
   */
  private extractProjectParam(url: string): string | null {
    const match = url.match(/\/project-management\/([^/?#(]+)/);
    if (!match) return null;
    const param = match[1];
    if (NON_PROJECT_SEGMENTS.has(param)) return null;
    return param;
  }

  /**
   * Résout le paramètre URL en UUID projet puis charge le contexte
   */
  private async resolveAndLoadProject(param: string): Promise<void> {
    let projectId: string;

    if (UUID_REGEX.test(param)) {
      projectId = param;
    } else {
      // L'URL contient un identifiant court → résoudre en UUID
      try {
        const project = await firstValueFrom(
          this.projectService.getProjectByIdentifier(param)
        );
        if (!project?.id) return;
        projectId = project.id;
      } catch {
        return;
      }
    }

    if (projectId !== this._currentProjectId()) {
      this._currentProjectId.set(projectId);
      this.loadProjectContext(projectId);
    }
  }

  private loadProjectContext(projectId: string): void {
    this.projectService.getProjectMembers(projectId).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (members) => {
        this._projectMembers.set(
          members
            .filter((m: any) => m.profile)
            .map((m: any) => ({
              profileId: m.profile_id || m.profile?.id,
              firstName: m.profile?.first_name || '',
              lastName: m.profile?.last_name || '',
              email: m.profile?.email || ''
            }))
        );
      },
      error: () => this._projectMembers.set([])
    });

    this.projectService.getProjectById(projectId).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (project) => {
        this._currentProjectName.set(project?.name || null);
      },
      error: () => this._currentProjectName.set(null)
    });
  }
}